import { useMemo } from 'react';
import type { Booking, SlotOverride } from '@api/types';
import {
  DOW_ES_SHORT,
  HOURS,
  isoDate,
  fmtTime,
  eventStyle,
  overrideStyle,
  bookingTone,
} from './calendar-shared';

interface CalendarWeekProps {
  monday: Date;
  bookings: readonly Booking[];
  overrides: readonly SlotOverride[];
  onOpenBooking: (id: string) => void;
  onDeleteOverride: (id: string) => void;
}

function buildWeekDays(monday: Date) {
  const today = isoDate(new Date());
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const iso = isoDate(d);
    return { iso, dow: DOW_ES_SHORT[i] ?? '', date: d.getDate(), today: iso === today };
  });
}

export function CalendarWeek({ monday, bookings, overrides, onOpenBooking, onDeleteOverride }: CalendarWeekProps) {
  const days = useMemo(() => buildWeekDays(monday), [monday]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, Booking[]> = {};
    for (const b of bookings) {
      const day = b.startTime.slice(0, 10);
      (map[day] ??= []).push(b);
    }
    return map;
  }, [bookings]);

  const overridesByDay = useMemo(() => {
    const map: Record<string, SlotOverride[]> = {};
    for (const ov of overrides) {
      for (const d of days) {
        const dayStart = new Date(`${d.iso}T00:00:00.000Z`);
        const dayEnd   = new Date(`${d.iso}T23:59:59.999Z`);
        if (new Date(ov.startTime) < dayEnd && new Date(ov.endTime) > dayStart) {
          (map[d.iso] ??= []).push(ov);
        }
      }
    }
    return map;
  }, [overrides, days]);

  return (
    <div
      className="cal-week"
      style={{ '--cal-cols': String(days.length) } as React.CSSProperties}
    >
      <div className="cal-week-head">
        <div className="cal-week-head-cell" />
        {days.map((d) => (
          <div key={d.iso} className="cal-week-head-cell" data-today={d.today}>
            <span className="cal-week-head-day">{d.dow}</span>
            <span className="cal-week-head-date">{d.date}</span>
          </div>
        ))}
      </div>

      <div className="cal-week-body">
        <div className="cal-time-col">
          {HOURS.map((h) => (
            <div key={h} className="cal-time-slot">
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>
        {days.map((d) => (
          <div key={d.iso} className="cal-day-col">
            {HOURS.map((h) => <div key={h} className="cal-grid-slot" />)}

            {(overridesByDay[d.iso] ?? []).map((ov) => {
              const style = overrideStyle(ov.startTime, ov.endTime, d.iso);
              if (!style) return null;
              return (
                <div
                  key={ov.id}
                  className="cal-override"
                  style={style}
                  title={ov.reason ?? 'Bloqueado'}
                >
                  <span className="cal-override-label">
                    {fmtTime(ov.startTime)}–{fmtTime(ov.endTime)}
                    {ov.reason ? ` · ${ov.reason}` : ''}
                  </span>
                  <button
                    type="button"
                    className="cal-override-close"
                    onClick={() => {
                      if (confirm('¿Eliminar este bloqueo?')) {
                        onDeleteOverride(ov.id);
                      }
                    }}
                    title="Eliminar bloqueo"
                  >
                    ✕
                  </button>
                </div>
              );
            })}

            {(eventsByDay[d.iso] ?? []).map((b) => (
              <button
                key={b.id}
                type="button"
                className="cal-event"
                data-tone={bookingTone(b.status)}
                style={{ ...eventStyle(b.startTime, b.endTime), cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
                title={`${b.customerName} · ${b.serviceName}`}
                onClick={() => { onOpenBooking(b.id); }}
              >
                <span className="cal-event-time">{fmtTime(b.startTime)} · {Math.round((new Date(b.endTime).getTime() - new Date(b.startTime).getTime()) / 60000)}m</span>
                <span className="cal-event-title">{b.serviceName}</span>
                <span className="cal-event-sub">{b.customerName.split(' ')[0]} {b.staffName ? `· ${b.staffName.split(' ')[0]}` : ''}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
